import React from 'react';
import { Link } from 'react-router-dom';

const Section = ({ title, children }) => (
  <section className="mb-10">
    <h2 className="text-xl font-bold mb-4 text-white border-b border-white/10 pb-3">{title}</h2>
    <div className="text-gray-400 space-y-3 leading-relaxed">{children}</div>
  </section>
);

const Table = ({ headers, rows }) => (
  <div className="overflow-x-auto mt-3">
    <table className="w-full text-sm border-collapse">
      <thead>
        <tr>
          {headers.map((h) => (
            <th key={h} className="text-left text-xs font-bold tracking-wider text-gray-300 bg-white/5 border border-white/10 px-4 py-3">
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i} className="border-b border-white/5 hover:bg-white/3 transition-colors">
            {row.map((cell, j) => (
              <td key={j} className="border border-white/8 px-4 py-3 text-gray-400 align-top">
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const QuickRow = ({ q, a, yes }) => (
  <tr className="border-b border-white/5">
    <td className="px-4 py-3 text-gray-300 font-medium">{q}</td>
    <td className="px-4 py-3">
      <span className={`font-bold ${yes ? 'text-green-400' : 'text-red-400'}`}>{a}</span>
    </td>
  </tr>
);

const DocBladePrivacyPage = () => (
  <div className="min-h-screen pt-24 pb-20">
    {/* Header */}
    <div className="py-14 relative" style={{ background: 'linear-gradient(135deg, #2563eb22 0%, transparent 70%)' }}>
      <div className="max-w-4xl mx-auto px-6">
        <Link to="/legal" className="text-sm text-gray-500 hover:text-white mb-6 inline-flex items-center gap-2 transition-colors">
          ← Back to Legal Hub
        </Link>
        <div className="flex items-center gap-4 mt-4">
          <span className="text-5xl">📄</span>
          <div>
            <p className="text-xs font-bold tracking-widest uppercase mb-1 text-blue-400">Privacy Policy</p>
            <h1 className="text-3xl md:text-4xl font-bold text-white">DocBlade: PDF Editor & Tools</h1>
            <p className="text-gray-500 text-sm mt-1">com.docblade.app · Productivity</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-3 mt-6 text-xs text-gray-500">
          <span className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10">Effective: July 1, 2026</span>
          <span className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10">Last Updated: July 1, 2026</span>
          <span className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10">Version: 1.0</span>
          <span className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10">Platform: Google Play (Android)</span>
        </div>
      </div>
    </div>

    <div className="max-w-4xl mx-auto px-6 py-12">

      {/* Key Principle banner */}
      <div className="p-6 rounded-2xl mb-10 border border-blue-500/30 bg-blue-500/10">
        <div className="flex items-start gap-3">
          <span className="text-2xl flex-shrink-0">🔒</span>
          <div>
            <p className="text-blue-300 font-bold mb-1">Key Principle: 100% Offline. Zero Data Collection.</p>
            <p className="text-gray-300 text-sm leading-relaxed">
              DocBlade operates completely offline. We do not collect, store, transmit, or have access to any of your personal
              data, documents, or files. All processing occurs locally on your device.
            </p>
          </div>
        </div>
      </div>

      {/* Quick Summary */}
      <div className="mb-10 rounded-2xl border border-white/10 overflow-hidden">
        <div className="bg-white/5 px-5 py-4 border-b border-white/10">
          <h2 className="font-bold text-white text-lg">📋 Quick Summary for Users</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left px-5 py-3 text-gray-400 font-medium text-xs tracking-wider">Question</th>
                <th className="text-left px-5 py-3 text-gray-400 font-medium text-xs tracking-wider">Answer</th>
              </tr>
            </thead>
            <tbody>
              <QuickRow q="Does DocBlade collect my data?" a="❌ No" yes={false} />
              <QuickRow q="Do you see my documents?" a="❌ No" yes={false} />
              <QuickRow q="Are my files uploaded anywhere?" a="❌ No" yes={false} />
              <QuickRow q="Is my data shared with third parties?" a="❌ No" yes={false} />
              <QuickRow q="Do you track my usage?" a="❌ No" yes={false} />
              <QuickRow q="Do I need internet for core features?" a="❌ No" yes={false} />
              <QuickRow q="Is my data safe?" a="✅ Yes — it stays on my device" yes={true} />
            </tbody>
          </table>
        </div>
      </div>

      <Section title="1. Overview">
        <p>
          DocBlade: PDF Editor &amp; Tools ("DocBlade", "we", "our", or "us") is developed by{' '}
          <strong className="text-white">Paul Nkingwa Thomas · Riven Byte Studios</strong> and is committed to protecting
          your privacy. This Privacy Policy explains how we handle your information when you use our mobile application ("App").
        </p>
        <p>
          Since DocBlade is a fully offline application, no personal data, documents, or file content ever leaves your device or
          reaches our systems.
        </p>
      </Section>

      <Section title="2. Information We Do NOT Collect">
        <p>DocBlade does not collect, store, or process any personal information, including but not limited to:</p>
        <Table
          headers={['Category', 'Details']}
          rows={[
            ['Personal Identifiable Information', 'Name, email address, phone number, physical address, or any identifying information'],
            ['Document Content', 'PDF files, converted documents, scanned images, text content, or any files you process'],
            ['Device Information', 'Device ID, IMEI, MAC address, or unique device identifiers'],
            ['Usage Data', 'How you use the app, feature interactions, session duration, or analytics'],
            ['Location Data', 'GPS, IP address, or any location-based information'],
            ['Financial Information', 'Payment details, credit card numbers, or transaction history'],
            ['Biometric Data', 'Fingerprints, facial recognition, or voice data'],
          ]}
        />
      </Section>

      <Section title="3. Local Processing & Data Storage">
        <h3 className="text-white font-semibold mt-4 mb-2">3.1 Local Processing</h3>
        <p>All file processing, including:</p>
        <ul className="list-disc list-inside mt-2 space-y-1 ml-2">
          <li>PDF editing and annotation</li>
          <li>Document conversion (to/from PDF)</li>
          <li>OCR (Optical Character Recognition)</li>
          <li>File merging, splitting, and compression</li>
          <li>Digital signing and form filling</li>
        </ul>
        <p className="mt-3">
          is performed entirely on your device. No data is uploaded to any server, cloud service, or third party.
        </p>

        <h3 className="text-white font-semibold mt-5 mb-2">3.2 Local Storage</h3>
        <ul className="list-disc list-inside space-y-1 ml-2">
          <li>Files you open or create are stored locally on your device.</li>
          <li>Settings and preferences are stored locally on your device.</li>
          <li>No data is stored on our servers or any external infrastructure.</li>
        </ul>

        <h3 className="text-white font-semibold mt-5 mb-2">3.3 Permissions</h3>
        <p>DocBlade requests the following permissions solely for core functionality:</p>
        <Table
          headers={['Permission', 'Purpose']}
          rows={[
            ['READ_EXTERNAL_STORAGE', 'To open and access PDF files from your device'],
            ['WRITE_EXTERNAL_STORAGE', 'To save processed files back to your device'],
            ['CAMERA', 'To scan documents directly into PDFs'],
            ['MANAGE_EXTERNAL_STORAGE', 'To enable full file access on Android 11+ (optional)'],
          ]}
        />
        <p className="mt-3 text-sm text-gray-500 italic">
          All permissions are used exclusively for local operations. We do not access, view, or transmit your files or data.
        </p>
      </Section>

      <Section title="4. Third-Party Services">
        <p>DocBlade does not integrate with any third-party services, including:</p>
        <ul className="list-disc list-inside mt-2 space-y-1 ml-2">
          <li>Analytics SDKs (Google Analytics, Firebase, etc.)</li>
          <li>Advertising networks</li>
          <li>Cloud storage services</li>
          <li>Data collection frameworks</li>
        </ul>
        <div className="mt-4 p-4 rounded-xl bg-green-500/8 border border-green-500/20">
          <p className="text-green-300 font-semibold text-sm">✅ Your data never leaves your device.</p>
        </div>
      </Section>

      <Section title="5. Internet & Network Usage">
        <p>
          DocBlade does not require internet access for any core functionality. While you may have internet connectivity on your
          device, the App:
        </p>
        <ul className="list-disc list-inside mt-2 space-y-1 ml-2">
          <li>Does not transmit any data over the network</li>
          <li>Does not ping external servers</li>
          <li>Does not check for cloud-based license validation</li>
        </ul>
        <p className="mt-3">
          Network permission (if present in the manifest) is unused or used only for crash reporting (if explicitly enabled by the
          user) and App Store review compliance (minimal, optional).
        </p>
      </Section>

      <Section title="6. Data Security">
        <p>
          Since all data remains on your device, security is your responsibility. We recommend:
        </p>
        <ul className="list-disc list-inside mt-2 space-y-1 ml-2">
          <li>Securing your device with a lock screen</li>
          <li>Using Android's built-in encryption</li>
          <li>Regularly backing up important files</li>
        </ul>
        <p className="mt-3">
          We do not access, process, or store your data on any external infrastructure, eliminating the risk of data breaches
          through our systems.
        </p>
      </Section>

      <Section title="7. Children's Privacy">
        <p>
          DocBlade is not directed at children under the age of 13. We do not knowingly collect personal information from anyone
          under 13. Since we do not collect any data, there is no risk of children's data being misused.
        </p>
      </Section>

      <Section title="8. Changes to This Privacy Policy">
        <p>
          We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated effective date.
        </p>
        <p>
          If you do not agree with any changes, you may uninstall the App. Continued use of DocBlade constitutes acceptance of the
          updated policy.
        </p>
      </Section>

      <Section title="9. Your Rights">
        <p>Since we do not collect any personal data, you are not required to:</p>
        <ul className="list-disc list-inside mt-2 space-y-1 ml-2">
          <li>Request data deletion</li>
          <li>Request data export</li>
          <li>Opt out of data collection</li>
          <li>Correct personal information</li>
        </ul>
        <p className="mt-3">You retain complete control over your data at all times.</p>
      </Section>

      <Section title="10. Data Protection Compliance">
        <p>
          DocBlade complies with the following privacy regulations by virtue of collecting no personal data:
        </p>
        <Table
          headers={['Regulation', 'Compliance Status']}
          rows={[
            ['GDPR (EU)', '✅ Fully compliant — No personal data collected'],
            ['CCPA/CPRA (California)', '✅ Fully compliant — No personal data collected'],
            ['PIPEDA (Canada)', '✅ Fully compliant — No personal data collected'],
            ['LGPD (Brazil)', '✅ Fully compliant — No personal data collected'],
            ["Children's Online Privacy Protection Act", '✅ Fully compliant — No data collected'],
          ]}
        />
      </Section>

      <Section title="11. Developer Contact">
        <p>If you have any questions, concerns, or requests regarding this Privacy Policy, please contact:</p>
        <div className="mt-4 p-5 rounded-xl bg-white/5 border border-white/10 space-y-2">
          <p><span className="text-gray-500">Developer:</span> <span className="text-white">Paul Nkingwa Thomas</span></p>
          <p><span className="text-gray-500">Studio:</span> <span className="text-white">Riven Byte Studios</span></p>
          <p>
            <span className="text-gray-500">Email:</span>{' '}
            <a href="mailto:paulnkingwa34@gmail.com" className="text-blue-400 underline">paulnkingwa34@gmail.com</a>
          </p>
          <p>
            <span className="text-gray-500">Website:</span>{' '}
            <a href="https://riven-byte.pages.dev" className="text-blue-400 underline">riven-byte.pages.dev</a>
          </p>
          <p><span className="text-gray-500">Location:</span> <span className="text-gray-300">Dar es Salaam, Tanzania</span></p>
        </div>
      </Section>

      <Section title="12. Consent">
        <p>
          By using DocBlade, you consent to this Privacy Policy. If you do not agree with this policy, please do not install or
          use the App.
        </p>
      </Section>

      <Section title="13. Special Notes for Google Play Store">
        <h3 className="text-white font-semibold mt-4 mb-2">13.1 Privacy Policy URL</h3>
        <p>
          This Privacy Policy is available online at:{' '}
          <a href="https://riven-byte.pages.dev/docblade/privacy" className="text-blue-400 underline">
            https://riven-byte.pages.dev/docblade/privacy
          </a>
        </p>

        <h3 className="text-white font-semibold mt-5 mb-2">13.2 Data Safety Section</h3>
        <p>Based on this Privacy Policy, you can declare in Google Play Console:</p>
        <div className="mt-3 grid sm:grid-cols-2 gap-2">
          {[
            'No User Data Collected', 'No Personal Information', 'No Location Data',
            'No Web Browsing History', 'No Contacts', 'No Calendar Data',
            'No SMS or Call Logs', 'No Photos/Media (processed locally only)',
            'No Audio Files (processed locally only)', 'No Financial Information',
            'No Device/Network Information',
          ].map((item) => (
            <div key={item} className="flex items-center gap-2 text-sm text-green-300 bg-green-500/8 border border-green-500/15 rounded-lg px-3 py-2">
              <span className="text-green-400 flex-shrink-0">✅</span>
              <span>{item}</span>
            </div>
          ))}
        </div>

        <h3 className="text-white font-semibold mt-5 mb-2">13.3 User Data Handling</h3>
        <p>All data processed by DocBlade:</p>
        <ul className="list-disc list-inside mt-2 space-y-1 ml-2">
          <li>Stays on the user's device</li>
          <li>Is never transmitted</li>
          <li>Is never shared with third parties</li>
          <li>Is never sold or used for advertising</li>
        </ul>
      </Section>

      <Section title="14. Disclaimer">
        <p>
          "DocBlade" and "Riven Byte Studios" are owned and operated by Paul Nkingwa Thomas.
        </p>
        <p>
          This Privacy Policy is provided for informational purposes and is not legal advice. If you have specific legal
          concerns, please consult with a qualified attorney.
        </p>
        <p className="text-gray-500 text-sm mt-4">
          Effective Date: July 1, 2026 · Last Updated: July 1, 2026 · Version: 1.0
        </p>
      </Section>

    </div>
  </div>
);

export default DocBladePrivacyPage;
